import { useMemo, useState, useEffect, useCallback, useRef } from "react";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { ArrowsUpDownIcon } from "@heroicons/react/24/outline";
import toast from "react-hot-toast";

import DeleteConfirmModal from "./DeleteConfirmModal";
import {
  EditableTextCell,
  EditableNumberCell,
  EditableCheckboxCell,
} from "./EditableCells";
import TagSelectDropdown from "../TagSelectDropdown";
import api from "../../utilities/api";

const columnHelper = createColumnHelper();

const TagTable = () => {
  const [data, setData] = useState([]);
  const [users, setUsers] = useState([]);
  const [tagCounts, setTagCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [sorting, setSorting] = useState([{ id: "sortOrder", desc: false }]);
  const [isCreating, setIsCreating] = useState(false);
  const [newTagName, setNewTagName] = useState("");
  const newTagInputRef = useRef(null);
  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    tagId: null,
    tagName: "",
  });

  // Fetch tag usage counts
  const fetchTagCounts = useCallback(async () => {
    try {
      const response = await api.get(`/tags/counts`);
      setTagCounts(response.data);
    } catch (error) {
      console.error("Error fetching tag counts:", error);
    }
  }, []);

  // Fetch tags
  const fetchTags = useCallback(async () => {
    try {
      setLoading(true);
      const [tagsResponse, usersResponse] = await Promise.all([
        api.get(`/tags`),
        api.get(`/users`),
        fetchTagCounts(),
      ]);
      setData(tagsResponse.data);
      setUsers(usersResponse.data);
    } catch (error) {
      console.error("Error fetching tags:", error);
      toast.error("Failed to load tags");
    } finally {
      setLoading(false);
    }
  }, [fetchTagCounts]);

  useEffect(() => {
    fetchTags();
  }, [fetchTags]);

  // Focus new tag input when creating
  useEffect(() => {
    if (isCreating && newTagInputRef.current) {
      newTagInputRef.current.focus();
    }
  }, [isCreating]);

  // Save field update
  const handleSave = useCallback(
    async (tagId, field, value) => {
      try {
        await api.patch(`/tags/${tagId}`, {
          [field]: value,
        });
        setData((prev) =>
          prev.map((tag) =>
            tag._id === tagId ? { ...tag, [field]: value } : tag,
          ),
        );
        toast.success("Tag updated");
      } catch (error) {
        console.error("Error updating tag:", error);
        toast.error(error.response?.data?.message || "Failed to update tag");
        fetchTags(); // Revert
      }
    },
    [fetchTags],
  );

  // Create new tag
  const handleCreateTag = useCallback(async () => {
    const name = newTagName.trim();
    if (!name) {
      setIsCreating(false);
      setNewTagName("");
      return;
    }

    try {
      const response = await api.post(`/tags`, {
        name,
        isFilter: false,
        sortOrder: 0,
      });
      setData((prev) => [response.data, ...prev]);
      toast.success("Tag created");
    } catch (error) {
      console.error("Error creating tag:", error);
      toast.error(error.response?.data?.message || "Failed to create tag");
    } finally {
      setIsCreating(false);
      setNewTagName("");
    }
  }, [newTagName]);

  // Delete tag
  const handleDelete = useCallback(async () => {
    const { tagId } = deleteModal;
    if (!tagId) return;

    try {
      await api.delete(`/tags/${tagId}`);
      setData((prev) => prev.filter((tag) => tag._id !== tagId));
      setTagCounts((prev) => {
        const updated = { ...prev };
        delete updated[tagId];
        return updated;
      });
      toast.success("Tag deleted");
    } catch (error) {
      console.error("Error deleting tag:", error);
      toast.error(error.response?.data?.message || "Failed to delete tag");
      // Re-fetch counts so the UI reflects the current reality
      fetchTagCounts();
    } finally {
      setDeleteModal({ isOpen: false, tagId: null, tagName: "" });
    }
  }, [deleteModal, fetchTagCounts]);

  // Users as dropdown options, labelled by email
  const userOptions = useMemo(
    () => users.map((user) => ({ _id: user._id, name: user.email })),
    [users],
  );

  const columns = useMemo(
    () => [
      columnHelper.display({
        id: "actions",
        header: () => (
          <button
            onClick={() => setIsCreating(true)}
            className="py-0 px-2 float-right mr-0 text-center text-cyan-900 font-bold text-lg border-2 border-gray-500 bg-gray-300 hover:bg-gray-500 hover:text-white rounded-lg transition duration-300"
          >
            New
          </button>
        ),
        cell: () => null,
        size: 80,
      }),
      columnHelper.accessor("name", {
        header: "Name",
        cell: (info) => {
          const count = tagCounts[info.row.original._id] ?? 0;
          return (
            <span className="flex items-center">
              <EditableTextCell
                getValue={info.getValue}
                row={info.row}
                column={info.column}
                onSave={handleSave}
              />
              <span className="text-gray-400 text-xs whitespace-nowrap">
                ({count})
              </span>
            </span>
          );
        },
        // No fixed size: Name takes the remaining width
        minSize: 300,
      }),
      columnHelper.accessor("isFilter", {
        header: "Filter",
        cell: (info) => (
          <EditableCheckboxCell
            getValue={info.getValue}
            row={info.row}
            column={info.column}
            onSave={handleSave}
          />
        ),
        size: 110,
        enableSorting: false,
      }),
      columnHelper.accessor("isRestricted", {
        header: "Restricted",
        cell: (info) => (
          <EditableCheckboxCell
            getValue={info.getValue}
            row={info.row}
            column={info.column}
            onSave={handleSave}
          />
        ),
        size: 110,
        enableSorting: false,
      }),
      columnHelper.accessor("allowedUsers", {
        header: "Allowed Users",
        cell: (info) => {
          const tag = info.row.original;
          const isRestricted = tag.isRestricted ?? false;
          // Map ids to user options so selected users display by email
          const selectedUsers = (info.getValue() ?? []).map(
            (id) =>
              userOptions.find((user) => user._id === id) ?? {
                _id: id,
                name: id,
              },
          );
          return (
            <div
              className={isRestricted ? "" : "opacity-40 pointer-events-none"}
              title={
                isRestricted ? "" : "Enable Restricted to choose allowed users"
              }
            >
              <TagSelectDropdown
                availableTags={userOptions}
                selectedTags={selectedUsers}
                onSelectionChange={(ids) =>
                  handleSave(tag._id, "allowedUsers", ids)
                }
                labelPrefix="Users"
                bgColor="bg-transparent"
                readOnly={!isRestricted}
              />
            </div>
          );
        },
        size: 200,
        enableSorting: false,
      }),
      columnHelper.accessor("sortOrder", {
        header: "Order",
        cell: (info) => (
          <EditableNumberCell
            getValue={info.getValue}
            row={info.row}
            column={info.column}
            onSave={handleSave}
          />
        ),
        size: 120,
      }),
      columnHelper.display({
        id: "delete",
        header: "",
        cell: ({ row }) => {
          const count = tagCounts[row.original._id] ?? 0;
          const inUse = count > 0;
          return (
            <button
              onClick={() =>
                setDeleteModal({
                  isOpen: true,
                  tagId: row.original._id,
                  tagName: row.original.name,
                })
              }
              disabled={inUse}
              title={
                inUse
                  ? `Cannot delete \u2014 used by ${count} record${count === 1 ? "" : "s"}`
                  : "Delete this tag"
              }
              className={`py-0 px-2 text-center font-normal text-sm border-2 rounded-lg transition duration-300 ${
                inUse
                  ? "text-gray-400 border-gray-200 bg-gray-50 cursor-not-allowed"
                  : "text-red-700 border-red-300 bg-white hover:bg-red-50 hover:border-red-500"
              }`}
            >
              Delete
            </button>
          );
        },
        size: 80,
      }),
    ],
    [handleSave, tagCounts, userOptions],
  );

  const table = useReactTable({
    data,
    columns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    autoResetPageIndex: false,
    initialState: {
      pagination: { pageSize: 10 },
    },
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64 text-gray-500">
        Loading tags...
      </div>
    );
  }

  return (
    <div className="p-2 text-black fill-gray-600">
      <div className="mt-2 flex flex-col">
        <div className="overflow-x-auto">
          <div className="inline-block min-w-full py-2 align-middle">
            <table className="min-w-full text-left">
              <thead className="bg-cyan-900 text-white">
                {table.getHeaderGroups().map((headerGroup) => (
                  <tr key={headerGroup.id}>
                    {headerGroup.headers.map((header) => (
                      <th
                        key={header.id}
                        style={{
                          width: header.column.columnDef.size,
                          minWidth: header.column.columnDef.minSize,
                        }}
                        className="py-2 px-2 whitespace-nowrap"
                      >
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )}
                        {header.column.getCanSort() && (
                          <button
                            onClick={header.column.getToggleSortingHandler()}
                          >
                            <ArrowsUpDownIcon
                              className="h-6 w-6 text-white relative"
                              style={{ top: "6px" }}
                            />
                          </button>
                        )}
                        {
                          {
                            asc: "🔼",
                            desc: "🔽",
                          }[header.column.getIsSorted()]
                        }
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody>
                {/* Inline new tag row */}
                {isCreating && (
                  <tr className="bg-cyan-50 border-b-2 border-cyan-300">
                    <td className="py-1 px-2"></td>
                    <td className="py-1 px-2">
                      <input
                        ref={newTagInputRef}
                        type="text"
                        value={newTagName}
                        onChange={(e) => setNewTagName(e.target.value)}
                        onBlur={handleCreateTag}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") e.target.blur();
                          if (e.key === "Escape") {
                            setIsCreating(false);
                            setNewTagName("");
                          }
                        }}
                        placeholder="Enter tag name..."
                        className="bg-white border border-cyan-400 rounded px-2 py-1 w-full text-sm focus:outline-none focus:border-cyan-600"
                      />
                    </td>
                    <td className="py-1 px-2">
                      <span className="text-xs text-gray-400">—</span>
                    </td>
                    <td className="py-1 px-2">
                      <span className="text-xs text-gray-400">—</span>
                    </td>
                    <td className="py-1 px-2">
                      <span className="text-xs text-gray-400">—</span>
                    </td>
                    <td className="py-1 px-2">
                      <span className="text-xs text-gray-400">—</span>
                    </td>
                    <td className="py-1 px-2"></td>
                  </tr>
                )}
                {table.getRowModel().rows.length ? (
                  table.getRowModel().rows.map((row, i) => (
                    <tr
                      key={row.id}
                      className={i % 2 === 0 ? "bg-slate-100" : "bg-slate-300"}
                    >
                      {row.getVisibleCells().map((cell) => (
                        <td key={cell.id} className="py-1 px-2">
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext(),
                          )}
                        </td>
                      ))}
                    </tr>
                  ))
                ) : (
                  <tr className="text-center h-32">
                    <td colSpan={columns.length}>No tags found</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-end mt-2 gap-2">
        <button
          onClick={() => table.firstPage()}
          disabled={!table.getCanPreviousPage()}
          className="p-1 border border-gray-300 px-2 disabled:opacity-30"
        >
          {"<<"}
        </button>
        <button
          onClick={() => table.previousPage()}
          disabled={!table.getCanPreviousPage()}
          className="p-1 border border-gray-300 px-2 disabled:opacity-30"
        >
          {"<"}
        </button>
        <button
          onClick={() => table.nextPage()}
          disabled={!table.getCanNextPage()}
          className="p-1 border border-gray-300 px-2 disabled:opacity-30"
        >
          {">"}
        </button>
        <button
          onClick={() => table.lastPage()}
          disabled={!table.getCanNextPage()}
          className="p-1 border border-gray-300 px-2 disabled:opacity-30"
        >
          {">>"}
        </button>
        <span className="flex items-center gap-1">
          <input
            type="number"
            value={table.getState().pagination.pageIndex + 1}
            onChange={(e) => {
              const page = e.target.value ? Number(e.target.value) - 1 : 0;
              table.setPageIndex(page);
            }}
            className="border text-right rounded w-14 bg-transparent"
          />{" "}
          of {table.getPageCount()}
        </span>
        <select
          value={table.getState().pagination.pageSize}
          onChange={(e) => table.setPageSize(Number(e.target.value))}
          className="p-2 bg-transparent"
        >
          {[10, 25, 50].map((pageSize) => (
            <option key={pageSize} value={pageSize}>
              Show {pageSize}
            </option>
          ))}
        </select>
      </div>

      <DeleteConfirmModal
        isOpen={deleteModal.isOpen}
        onClose={() =>
          setDeleteModal({ isOpen: false, tagId: null, tagName: "" })
        }
        onConfirm={handleDelete}
        title="Delete Tag"
        message={`Are you sure you want to delete "${deleteModal.tagName}"? This may affect records using this tag.`}
      />
    </div>
  );
};

export default TagTable;
